import {
    codicon,
    CompositeTreeNode,
    ExpandableTreeNode,
    SelectableTreeNode,
    TreeNode,
    TreeProps
} from '@theia/core/lib/browser';
import { ContextMenuRenderer, NodeProps } from '@theia/core/lib/browser';
import { TREE_NODE_SEGMENT_CLASS, TREE_NODE_TAIL_CLASS } from '@theia/core/lib/browser/tree/tree-widget';
import { inject, injectable } from '@theia/core/shared/inversify';
import * as React from '@theia/core/shared/react';
import { DirNode } from '@theia/filesystem/lib/browser';
import { FileNavigatorModel } from '@theia/navigator/lib/browser/navigator-model';
import { FileNavigatorWidget } from '@theia/navigator/lib/browser/navigator-widget';
import URI from '@theia/core/lib/common/uri';
import { TemplateRoot } from './template-create-commands';
import { TemplateCreateService } from './template-create-service';
import { isBrowserOnlyTheia } from '../online-preview/environment';

const TOOLTIP_BY_ROOT: Record<TemplateRoot, string> = {
    components: 'Add New Component',
    screens: 'Add New Screen',
    widgets: 'Add New Widget',
    tests: 'Add New Test'
};

const BROWSER_ONLY_THEIA: boolean = isBrowserOnlyTheia();

@injectable()
export class LvglFileNavigatorWidget extends FileNavigatorWidget {
    @inject(TemplateCreateService)
    protected readonly templateCreateService!: TemplateCreateService;

    protected autoExpandedWorkspaceRootId: string | undefined;
    protected expandingWorkspaceRootId: string | undefined;

    constructor(
        @inject(TreeProps) props: TreeProps,
        @inject(FileNavigatorModel) override readonly model: FileNavigatorModel,
        @inject(ContextMenuRenderer) contextMenuRenderer: ContextMenuRenderer
    ) {
        super(props, model, contextMenuRenderer);
    }

    protected override doUpdateRows(): void {
        this.expandSingleWorkspaceRoot();
        super.doUpdateRows();
    }

    protected override renderTailDecorations(node: TreeNode, props: NodeProps): React.ReactNode {
        const decorations: React.ReactNode = super.renderTailDecorations(node, props);
        if (BROWSER_ONLY_THEIA) return decorations;

        const templateRoot: TemplateRoot | undefined = this.getTemplateRootForNode(node);
        if (!templateRoot || !DirNode.is(node)) return decorations;

        const button: React.JSX.Element = (
            <div
                key="lvgl-template-create"
                className={`${TREE_NODE_SEGMENT_CLASS} ${TREE_NODE_TAIL_CLASS} lvgl-template-create-button`}
                title={TOOLTIP_BY_ROOT[templateRoot]}
                onClick={(event: React.MouseEvent) => this.handleCreateClick(event, templateRoot, node.uri)}
            >
                <span className={codicon('add')} />
            </div>
        );

        return (
            <>
                {decorations}
                {button}
            </>
        );
    }

    protected handleCreateClick(event: React.MouseEvent, templateRoot: TemplateRoot, parentUri: URI): void {
        event.preventDefault();
        event.stopPropagation();
        void this.templateCreateService.create(templateRoot, parentUri);
    }

    protected getTemplateRootForNode(node: TreeNode): TemplateRoot | undefined {
        if (!DirNode.is(node)) return undefined;

        return this.templateCreateService.getTemplateRootForUri(node.uri);
    }

    protected expandSingleWorkspaceRoot(): void {
        // The browser workspace has one synthetic root (for now):
        // - expand it once so loaded files are visible
        // - then respect manual collapse.
        const modelRoot: TreeNode | undefined = this.model.root;
        if (!CompositeTreeNode.is(modelRoot) || modelRoot.children.length !== 1) {
            this.autoExpandedWorkspaceRootId = undefined;
            return;
        }

        const workspaceRoot: TreeNode = modelRoot.children[0];
        if (!ExpandableTreeNode.is(workspaceRoot) || !SelectableTreeNode.is(workspaceRoot)) return;

        if (this.model.selectedNodes.length === 0 && !workspaceRoot.selected) {
            this.model.selectNode(workspaceRoot);
        }

        if (
            workspaceRoot.expanded ||
            this.autoExpandedWorkspaceRootId === workspaceRoot.id ||
            this.expandingWorkspaceRootId === workspaceRoot.id
        ) {
            return;
        }

        this.expandingWorkspaceRootId = workspaceRoot.id;
        void this.model
            .expandNode(workspaceRoot)
            .then((): void => {
                this.autoExpandedWorkspaceRootId = workspaceRoot.id;
            })
            .catch((): void => undefined)
            .finally((): void => {
                if (this.expandingWorkspaceRootId === workspaceRoot.id) {
                    this.expandingWorkspaceRootId = undefined;
                }
            });
    }
}
